import { Box, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { isNil } from "lodash";

import {
  Cancel,
  CheckCircle,
  CloudDoneOutlined,
  CloudOff,
  SignalCellularOff,
  SignalWifiOff,
  Warning,
} from "@mui/icons-material";

import {
  EthernetIcon,
  NetworkSignal,
  WifiSignal,
} from "empower-energy-owner-components";
import SiteStatusIcon from "./SiteStatusIcon";

const styles = (theme) => {
  return {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    minWidth: 0,
    color: theme.palette.primary.main,
    "& .wizard-status-child": {
      marginRight: "4px",
      height: "32px",
    },
    "& .wizard-status-child-icon": {
      marginTop: "4px",
    },
  };
};

const STATUS = { SUCCESS: "SUCCESS", WARNING: "WARNING", ERROR: "ERROR" };

const SiteStatus = ({ status }) => {
  const [statusCheck, setStatusCheck] = useState(STATUS.ERROR);
  const [statusTooltip, setStatusTooltip] = useState("Online");
  const [alias, setAlias] = useState("");

  const aliases = useSelector((state) => state?.profile?.aliases);

  useEffect(() => {
    // determine status
    if (
      status.running &&
      status.mqtt_connected &&
      status.grid_connected &&
      !status.halted &&
      !status.faulted
    ) {
      setStatusCheck(STATUS.SUCCESS);
    } else if (status.running && status.mqtt_connected && !status.halted) {
      setStatusCheck(STATUS.WARNING);
    } else {
      setStatusCheck(STATUS.ERROR);
    }

    const tips = [];
    if (status.grid_backup) tips.push("Backup active");
    if (!status.grid_connected) tips.push("Grid disconnected");
    if (!status.running) tips.push("Not running");
    if (status.halted) tips.push("Halted");
    if (status.faulted) tips.push("Fault active");
    if (!status.mqtt_connected) tips.push("Network disconnected");

    setStatusTooltip(tips.length > 0 ? tips.join(", ") : "Online");
  }, [status]);

  useEffect(() => {
    if (aliases.length > 0) {
      const aliasUnit = aliases.find((a) => a.item === status.id);
      if (aliasUnit) setAlias(aliasUnit.name);
    }
  }, [aliases, status.id]);

  return (
    <Box sx={(theme) => styles(theme)}>
      <Tooltip title={statusTooltip} arrow>
        {statusCheck === STATUS.SUCCESS && (
          <CheckCircle
            className="wizard-status-child wizard-status-child-icon"
            sx={{ color: "success.main", fontSize: 20 }}
          />
        )}
        {statusCheck === STATUS.ERROR && (
          <Cancel
            className="wizard-status-child wizard-status-child-icon"
            sx={{ color: "error.main", fontSize: 20 }}
          />
        )}
        {statusCheck === STATUS.WARNING && (
          <Warning
            className="wizard-status-child wizard-status-child-icon"
            sx={{ color: "warning.main", fontSize: 20 }}
          />
        )}
      </Tooltip>

      <Box width="130px">
        <Link to={`/sites/details/${status.id}`} className="text-link">
          <Tooltip title="Site NMI">
            <Typography
              className="wizard-status-child"
              variant="subtitle1"
              alignItems="center"
              display="flex"
              color="primary"
              sx={{ height: "26px !important" }}
            >
              {status?.details?.nmi}
            </Typography>
          </Tooltip>
          {alias && (
            <Tooltip title={alias} arrow>
              <Typography
                className="overflow-ellipsis"
                fontSize="13px"
                lineHeight={1}
              >
                {alias}
              </Typography>
            </Tooltip>
          )}
        </Link>
      </Box>

      {status?.mqtt_connected ? (
        <Tooltip title="Connected" placement="top" arrow>
          <CloudDoneOutlined className="wizard-status-child" />
        </Tooltip>
      ) : (
        <Tooltip title="Disconnected" placement="top" arrow>
          <CloudOff className="wizard-status-child" />
        </Tooltip>
      )}
      {isNil(status.wifi) && isNil(status.ethernet) && (
        <Tooltip title="Network not configured" placement="top" arrow>
          <SignalWifiOff className="wizard-status-child" />
        </Tooltip>
      )}

      {!isNil(status.wifi) && (
        <>
          {status?.wifi === 0 ? (
            <Tooltip title="Wi-Fi disconnected" placement="top" arrow>
              <SignalWifiOff className="wizard-status-child" />
            </Tooltip>
          ) : (
            <Tooltip title="Wi-Fi connected" placement="top" arrow>
              <WifiSignal signal={status?.wifi} />
            </Tooltip>
          )}
        </>
      )}

      {!isNil(status.ethernet) && (
        <>
          {status?.ethernet === 0 ? (
            <Tooltip title="Ethernet disconnected" placement="top" arrow>
              <EthernetIcon configured={false} />
            </Tooltip>
          ) : (
            <Tooltip title="Ethernet connected" placement="top" arrow>
              <EthernetIcon configured={true} />
            </Tooltip>
          )}
        </>
      )}

      {isNil(status?.modem) || status?.modem === 0 ? (
        <Tooltip title="Fallback network unavailable" placement="top" arrow>
          <SignalCellularOff className="wizard-status-child" />
        </Tooltip>
      ) : (
        <Tooltip title="Fallback network available" placement="top" arrow>
          <NetworkSignal signal={status?.modem} />
        </Tooltip>
      )}

      {status?.running_description && (
        <Box mr={2}>
          <SiteStatusIcon
            type="operation"
            value1={status?.running_description}
            value2={status?.running_sub_description}
            tooltips={["Running"]}
            width="unset"
          />
        </Box>
      )}
    </Box>
  );
};

export default SiteStatus;
